/**
 * MechaGuard - 敌人类型注册表
 *
 * 5 种敌人仅通过数值与外观参数区分，因此采用数据驱动方式统一配置，
 * 而不是为每种敌人写一个近乎重复的子类。
 *
 * 数值说明
 *   hp      : 基础生命值（会乘以波次成长倍率）
 *   speed   : 移动速度（像素/秒）
 *   armor   : 减伤比例 0~1（0.3 = 减少 30% 伤害）
 *   reward  : 击杀获得金币
 *   radius  : 碰撞/绘制半径
 *   shape   : 机体绘制形状
 *   regen   : 每秒回血（仅精英怪）
 *   phases  : 多阶段变身（仅 BOSS）—— 生命比例跌破 at 时触发，
 *             可改变速度倍率 / 护甲加成 / 回血 / 颜色
 */

import { balanceConfig } from '../config/BalanceConfig.js';

export const EnemyTypes = Object.freeze({
  scout: {
    id: 'scout',
    name: '侦察兵',
    hp: 50,
    speed: 132,
    armor: 0,
    reward: 10,
    radius: 11,
    color: '#00ff88',
    shape: 'triangle',
    desc: '速度极快，血量很低',
  },

  grunt: {
    id: 'grunt',
    name: '杂兵',
    hp: 150,
    speed: 92,
    armor: 0.10,
    reward: 25,
    radius: 13,
    color: '#3aa0ff',
    shape: 'square',
    desc: '标准作战单位，属性均衡',
  },

  heavy: {
    id: 'heavy',
    name: '重装机甲',
    hp: 400,
    speed: 56,
    armor: 0.30,
    reward: 50,
    radius: 17,
    color: '#ffcc00',
    shape: 'hexagon',
    desc: '高护甲厚血，推进缓慢',
  },

  elite: {
    id: 'elite',
    name: '精英怪',
    hp: 600,
    speed: 88,
    armor: 0.20,
    reward: 80,
    radius: 15,
    color: '#ff3366',
    shape: 'diamond',
    regen: 10,
    desc: '自带修复模块，持续回血',
  },

  boss: {
    id: 'boss',
    name: 'BOSS',
    hp: 3000,
    speed: 48,
    armor: 0.40,
    reward: 500,
    radius: 26,
    color: '#c04bff',
    shape: 'octagon',
    desc: '重装旗舰，每损失 25% 生命会进入新形态',
    /**
     * 多阶段变身：生命比例跌到 at 以下时触发。
     * 阶段效果会叠加在基础数值之上（速度乘算、护甲加算、覆盖回血）。
     */
    phases: [
      {
        at: 0.75,
        name: '过载',
        speedMul: 1.20,
        regen: 12,
        armorBonus: 0.05,
        color: '#d86bff',
      },
      {
        at: 0.50,
        name: '狂暴',
        speedMul: 1.45,
        regen: 25,
        armorBonus: 0.10,
        color: '#ff5ce0',
      },
      {
        at: 0.25,
        name: '终焉',
        speedMul: 1.75,
        regen: 45,
        armorBonus: 0.15,
        color: '#ff3366',
      },
    ],
  },
});

/** 按 id 取敌人配置（合并运营覆盖层，对下一波/新刷怪生效） */
export function getEnemyConfig(typeId) {
  const base = EnemyTypes[typeId];
  if (!base) throw new Error(`未知的敌人类型: ${typeId}`);
  return balanceConfig.getEnemyConfigMerged(typeId, base);
}

/** 全部敌人类型（用于图鉴 / 调试） */
export const ENEMY_LIST = Object.freeze(Object.values(EnemyTypes));
